import useMedia from '@/hooks/useMedia'
import classnames from 'classnames/bind'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
// Lib
import { ProductInfoType } from '@/api/fetchProductData'
import { useLiveData } from '@/hooks/useLiveData'
import { useObserve } from '@/hooks/useObserver'
import getDisplayPrice from '@/methods/getDisplayPrice'
import { handleGtmSelectItem } from '@/methods/handleGtmEvent'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import { BigNumber } from 'bignumber.js'

// Components
import DiscountRateTag from '@/components/shared/DiscountRateTag'
import Insight from '@/components/shared/Insight'
import Tags from '@/components/shared/Tags'
import Carousell from './components/Carousell'

// Constants
import { COMPONENT_NAMES } from '@/constants/componentNames'
import { GtmClickLiveRoomPosition } from '@/constants/gtmEvent'
import { ROUTE_PATH } from '@/constants/routePath'
import { isLiveMallClub } from '@/constants/systemType'

// Methods

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

export interface ITagItem {
  style: number
  name: string
}

type IProductCard = {
  className?: string
  tags: ITagItem[]
  handleClick?: () => void
  isPromo?: boolean
  isMiniWidth?: boolean
  observer?: IntersectionObserver
  showSaleQuantity?: boolean
  inlineStyle?: any
  gtmItemEventParams?: {
    itemListName: string
    itemListId?: string
  }
  useFrom?: string
} & ProductInfoType

const ProductCard = (props: IProductCard) => {
  const {
    images,
    minLivePrice,
    minPrice,
    minOrigPrice,
    productId,
    productName,
    saleQuantity,
    tags,
    className,
    isPromo = false,
    isMiniWidth = false,
    observer,
    gtmItemEventParams,
    showSaleQuantity = true,
    inlineStyle,
    handleClick,
    useFrom = '',
  } = props
  const { t } = useTranslation()
  const router = useRouter()
  const { isMobile } = useMedia()
  const { isLive, roomId, liveProductInfo } = useLiveData({ productId: String(productId) })

  const { displayPrice, productPrice, productOriginalPrice } = getDisplayPrice({
    isLive,
    origPrice: minOrigPrice,
    minPrice,
    livePrice: minLivePrice,
  })

  const productCardRef = useRef(null)
  const [showNormalDiscountRateTag, setShowNormalDiscountRateTag] = useState(true)
  useObserve({ observer: observer, targetRef: productCardRef.current })

  const slicedImages = images?.slice(0, 5)

  const handleGoToProductDetail = () => {
    if (useFrom === COMPONENT_NAMES.RECENT_LIVE_STREAMING && !isEmpty(gtmItemEventParams)) {
      // 發送 GA 事件：select_item
      handleGtmSelectItem({
        ...(gtmItemEventParams as { itemListName: string; itemListId?: string }),
        productInfoList: [
          {
            ...(props as ProductInfoType),
            price: String(productPrice),
          },
        ],
      })
    } else {
      handleGtmSelectItem({
        ...(gtmItemEventParams as { itemListName: string; itemListId?: string }),
        productInfoList: [
          {
            ...(props as ProductInfoType),
          },
        ],
      })
    }

    handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.PRODUCT}/${productId}`,isOpen: true })
  }

  useEffect(() => {
    if (isMiniWidth && isMobile) {
      setShowNormalDiscountRateTag(false)
    }
  }, [isMobile])

  return (
    <div
      ref={productCardRef}
      data-selector-id='product-card'
      onClick={() => {
        handleGoToProductDetail()
        typeof handleClick === 'function' && handleClick()
      }}
      className={cx('productCard', className)}
      style={inlineStyle}
      data-id={productId}
    >
      <a className={cx('productCard-link')} href={`${ROUTE_PATH.PRODUCT}/${productId}`} />

      <div className={cx('productCard__image')}>
        <Carousell
          images={slicedImages}
          tags={tags}
          handleClick={() => {
            handleGoToProductDetail()
            typeof handleClick === 'function' && handleClick()
          }}
        />
      </div>

      {isLive && (
        <Insight
          roomId={roomId}
          liveSourceType={liveProductInfo?.liveSourceType}
          liveType={liveProductInfo?.liveType}
          status='live'
          count={liveProductInfo?.dummyViews}
          theme='transparent-black'
          position='absolute'
          top='8px'
          left='8px'
          zIndex='2'
          gtmClickLiveRoomPosition={GtmClickLiveRoomPosition.ITEM_CARD}
        />
      )}

      <div className={cx('productCard__info')}>
        <div className={cx('productCard__info-header')}>
          {/* product name */}
          <div className={cx('productCard__name')}>{productName}</div>

          {/* tags */}
          {!isPromo && (
            <div className={cx('productCard__tags')}>
              <Tags tags={tags} max={3} />
            </div>
          )}
        </div>

        {/* footer */}
        <div className={cx('productCard__info-footer')}>
          {/* discount */}
          <div className={cx('productCard__discount')}>
            {isMobile && isMiniWidth && <DiscountRateTag productPrice={productPrice} productOriginalPrice={productOriginalPrice} />}
          </div>

          <div className={cx('productCard__discount')}>
            <div className={cx('productCard__discount-price')}>
              {isLiveMallClub ? `JPY` : '$'}
              {displayPrice}
            </div>
            {showNormalDiscountRateTag && (
              <DiscountRateTag productPrice={productPrice} productOriginalPrice={productOriginalPrice} gapPosition='left' />
            )}
          </div>

          {/* detail */}
          {showSaleQuantity && (
            <div className={cx('productCard__detail')}>
              <div className={cx('productCard__detail-sale-quantity', { 'productCard__detail-sale-quantity--empty': saleQuantity === 0 })}>
                {t('Product_Sold', { amount: new BigNumber(saleQuantity).toFormat() })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductCard
